import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { FaBars, FaBell, FaSearch, FaSignOutAlt } from 'react-icons/fa';
import { logout } from '../../features/auth/authSlice';

const DashboardHeader = ({ toggleSidebar, darkMode }) => {
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  // Sample notifications - in a real app, these would come from an API
  const notifications = [
    { id: 1, text: 'New course available: Advanced React', time: '2 hours ago', read: false },
    { id: 2, text: 'Your progress in JavaScript Basics is 75%', time: '1 day ago', read: true },
    { id: 3, text: 'Reminder: Complete your profile information', time: '3 days ago', read: true },
  ];

  const handleLogout = () => {
    dispatch(logout());
  };

  return (
    <header className={`${darkMode ? 'bg-gray-800 text-white' : 'bg-white'} shadow-sm z-20 relative`}>
      <div className="flex items-center justify-between px-4 py-3">
        {/* Left side - Menu toggle, logo and search */}
        <div className="flex items-center">
          <button
            onClick={toggleSidebar}
            className={`p-2 rounded-md ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'} lg:hidden`}
          >
            <FaBars />
          </button>

          <div className="flex items-center ml-4">
            <img 
              src="/logo.PNG" 
              alt="IntelUniVen Logo" 
              className="h-8 w-8 mr-3 rounded-full"
            />
            <span className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-primary'} font-solway hidden sm:block`}>IntelUNiven LMS</span>
          </div>

          <div className={`hidden md:flex items-center ml-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} container-rounded-lg px-3 py-2`}>
            <FaSearch className={`${darkMode ? 'text-gray-300' : 'text-gray-500'} mr-2`} />
            <input
              type="text"
              placeholder="Search..."
              className={`bg-transparent border-none focus:outline-none text-sm w-64 ${darkMode ? 'text-white placeholder-gray-400' : ''}`}
            />
          </div>
        </div>

        {/* Right side - Notifications and user menu */}
        <div className="flex items-center space-x-4">
          {/* Notifications */}
          <div className="relative">
            <button
              className={`p-2 rounded-full ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'} relative`}
              onClick={() => setShowNotifications(!showNotifications)}
            >
              <FaBell />
              {notifications.some(n => !n.read) && (
                <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-500"></span>
              )}
            </button>

            {/* Notifications dropdown */}
            {showNotifications && (
              <div className={`absolute right-0 mt-2 w-80 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white'} container-rounded-md shadow-lg py-1 z-30 border`}>
                <div className={`px-4 py-2 ${darkMode ? 'border-gray-700' : 'border-b'}`}>
                  <h3 className={`text-sm font-medium ${darkMode ? 'text-white' : ''}`}>Notifications</h3>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notifications.length > 0 ? (
                    notifications.map((notification) => (
                      <div
                        key={notification.id}
                        className={`px-4 py-3 ${darkMode ? `hover:bg-gray-700 border-gray-700 ${!notification.read ? 'bg-blue-900' : ''}` : `hover:bg-gray-50 border-b ${!notification.read ? 'bg-blue-50' : ''}`}`}
                      >
                        <p className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{notification.text}</p>
                        <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>{notification.time}</p>
                      </div>
                    ))
                  ) : (
                    <div className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'} text-center`}>
                      No notifications
                    </div>
                  )}
                </div>
                <div className={`px-4 py-2 ${darkMode ? 'border-gray-700' : 'border-t'} text-center`}>
                  <Link to="/dashboard/notifications" className="text-xs text-primary hover:underline">
                    View all notifications
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* User menu */}
          <div className="relative">
            <button
              className="flex items-center space-x-2"
              onClick={() => setShowUserMenu(!showUserMenu)}
            >
              <div className={`w-8 h-8 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} flex items-center justify-center text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {user?.avatar ? (
                  <img
                    src={user.avatar}
                    alt={user.name}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <span>{user?.name?.charAt(0).toUpperCase()}</span>
                )}
              </div>
              <span className={`hidden md:inline text-sm font-medium ${darkMode ? 'text-white' : ''}`}>{user?.name}</span>
            </button>

            {/* User dropdown */}
            {showUserMenu && (
              <div className={`absolute right-0 mt-2 w-48 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white'} container-rounded-md shadow-lg py-1 z-30 border`}>
                <Link
                  to="/dashboard/profile"
                  className={`block px-4 py-2 text-sm ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}
                  onClick={() => setShowUserMenu(false)}
                >
                  Your Profile
                </Link>
                <Link
                  to="/dashboard/settings"
                  className={`block px-4 py-2 text-sm ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}
                  onClick={() => setShowUserMenu(false)}
                >
                  Settings
                </Link>
                <button
                  onClick={handleLogout}
                  className={`block w-full text-left px-4 py-2 text-sm ${darkMode ? 'text-red-400 hover:bg-gray-700' : 'text-red-600 hover:bg-gray-100'}`}
                >
                  <div className="flex items-center">
                    <FaSignOutAlt className="mr-2" />
                    Sign out
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default DashboardHeader;